import { apexBarChartOptions } from "components/Common/constants"
import React, { useEffect, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { Bar, Pie } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { getEdiyahDashboard } from "store/actions/dashboardAction"
import { getGiftsCardList } from "store/actions/giftCardAction"

const GiftCard = ({
  giftCardId,
  dateRange,
  commonDayType,
  periodType,
  customDateRange,
  commonDateRange,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()
  const { loading, ediyahDashboardData } = useSelector(state => state.Dashboard)

  const [isLoading, setIsLoading] = useState(true)

  const fetchEidiyahDashboardData = () => {
    const updatedGiftCardIds = giftCardId?.map(item => item.value) || []
    const payload = {
      giftCardId: updatedGiftCardIds,
      dateRange: commonDateRange || {},
    }

    dispatch(getEdiyahDashboard(payload))
  }

  const fetchGiftCardList = () => {
    dispatch(getGiftsCardList())
  }

  useEffect(() => {
    fetchEidiyahDashboardData()
  }, [giftCardId, commonDateRange])

  useEffect(() => {
    fetchGiftCardList()
  }, [])

  const giftCardTopData = [
    {
      id: 1,
      title: "Total Purchase Amount",
      value: ediyahDashboardData?.overallData?.totalPurchaseAmount ?? 0,
    },
    {
      id: 2,
      title: "Total Purchased",
      value: ediyahDashboardData?.overallData?.totalPurchasedCount ?? 0,
    },
    {
      id: 3,
      title: "Total Redeemed Amount",
      value: Math?.round(
        ediyahDashboardData?.overallData?.totalRedeemedAmount ?? 0
      ),
    },
    {
      id: 4,
      title: "Total Redeemed",
      value: ediyahDashboardData?.overallData?.totalRedeemedCount ?? 0,
    },

    {
      id: 5,
      title: "Redeem Amount Rate",
      value: `${Math?.round(
        ediyahDashboardData?.overallData?.redeemAmountRate ?? 0
      )} %`,
    },
    {
      id: 6,
      title: "Average Eidiyah Value",
      value: Math?.round(
        ediyahDashboardData?.overallData?.averageGiftCardValue ?? 0
      ),
    },
    {
      id: 7,
      title: "Top Eidiyah",
      value:
        language === "sa"
          ? ediyahDashboardData?.overallData?.topGiftCardNameAr
          : ediyahDashboardData?.overallData?.topGiftCardName || "-",
    },
    // {
    //   id: 8,
    //   title: "Top Eidiyah Amount",
    //   value: ediyahDashboardData?.overallData?.topGiftCardAmount,
    // },
    {
      id: 8,
      title: "Redemption Rate",
      value: `${Math?.round(
        ediyahDashboardData?.overallData?.redemptionRate ?? 0
      )} %`,
    },
  ]

  const [pieData, setPieData] = useState({
    labels: ["Redeemed", "Unredeemed"],
    datasets: [
      {
        data: [0, 0], // Default values to avoid errors
        backgroundColor: ["#646A53", "#D3A984"],
        hoverBackgroundColor: ["#646A53", "#C09672"],
      },
    ],
  })

  const [chartOptions, setChartOptions] = useState(apexBarChartOptions)
  const [series, setSeries] = useState([])

  const [barData, setBarData] = useState({
    labels: [],
    datasets: [],
  })

  const [lineChartData, setLineChartData] = useState([])
  const [lineChartConfig, setLineChartConfig] = useState({
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: "straight", width: 2 },
    markers: { size: 5 },
    dataLabels: { enabled: false, style: { fontSize: "14px" } },
    xaxis: { categories: [] },
    colors: ["#646A53", "#34c38f"],
    tooltip: { enabled: true },
  })

  // Update data when API response comes
  useEffect(() => {
    setIsLoading(true)
    // Redeemed vs. Unredeemed
    if (ediyahDashboardData?.overallData) {
      setPieData({
        labels: [t("Redeemed"), t("Unredeemed")],
        datasets: [
          {
            data: [
              ediyahDashboardData.overallData.totalRedeemedCount || 0,
              ediyahDashboardData.overallData.totalUnredeemedCount || 0,
            ],
            backgroundColor: ["#00e396", "#008ffb"],
            hoverBackgroundColor: ["#00e396", "#008ffb"],
          },
        ],
      })
    }

    // Top Eidiyah Value
    if (ediyahDashboardData?.topGiftCardValues) {
      setSeries([
        {
          data:
            ediyahDashboardData?.topGiftCardValues?.map(item =>
              Math?.round(item?.averageGiftCardValue)
            ) || [],
        },
      ])

      setChartOptions(prev => ({
        ...prev,
        xaxis: {
          categories: ediyahDashboardData?.topGiftCardValues?.map(item =>
            language === "sa" ? item.nameAr : item.name
          ),
        },
      }))
    }

    //
    if (ediyahDashboardData?.groupedData) {
      setBarData({
        labels:
          ediyahDashboardData?.groupedData?.map(item =>
            language === "sa" ? item?.nameAr : item?.name
          ) || [], // X-axis labels
        datasets: [
          {
            label: t("Total Transactions"),
            data: ediyahDashboardData?.groupedData?.map(
              item => item?.purchasedCount
            ),
            backgroundColor: "#646A53",
          },
          {
            label: t("Total Amount"),
            data: ediyahDashboardData?.groupedData?.map(
              item => item?.totalPurchaseAmount
            ),
            backgroundColor: "#84C5B1",
          },
        ],
      })
    }

    if (ediyahDashboardData?.salesTrend) {
      const categories =
        ediyahDashboardData?.salesTrend?.map(item => item.timePeriod) || []

      setLineChartData([
        {
          name: "",
          data:
            ediyahDashboardData?.salesTrend?.map(item => item.totalSales) || [],
        },
      ])

      setLineChartConfig(prev => ({
        ...prev,
        xaxis: { categories },
      }))

      setLineChartConfig(prev => ({
        ...prev,
        xaxis: { categories },
        tooltip: {
          enabled: true,
          y: {
            formatter: (val, { dataPointIndex }) =>
              `${t("Total Sales")}: ${val} <br> ${t("Total Count")}: ${
                ediyahDashboardData?.salesTrend[dataPointIndex]?.totalCount
              }`,
          },
        },
      }))
    }

    setTimeout(() => {
      setIsLoading(false)
    }, 500)
  }, [ediyahDashboardData, language])

  const pieChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: tooltipItem => {
            const total = tooltipItem.dataset.data.reduce(
              (acc, value) => acc + value,
              0
            )
            const percentage = ((tooltipItem.raw / total) * 100).toFixed(1) // 1 decimal place
            return [
              `${tooltipItem.label}: ${tooltipItem.raw}`,
              `${t("Percentage")}: ${percentage}%`,
            ]
          },
        },
      },
    },
  }

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: context => `${context.dataset.label}: ${context.raw}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const getTotal = (data, field) => {
    return data?.reduce((sum, item) => sum + (item?.[field] || 0), 0)
  }

  return (
    <>
      <Row>
        {giftCardTopData?.map(item => (
          <Col key={item?.id} lg={3}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="text-center">{t(item?.title)}</CardTitle>
                {loading ? (
                  <ShimmerThumbnail height={30} rounded className="mb-0 pb-0" />
                ) : (
                  //   <h3>{dashboardOrderData?.cardData?.activeOrdersCount ?? 0}</h3>
                  <div style={{ direction: "ltr" }}>
                    <h4 className="text-center">{item.value}</h4>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="">
        <Col lg={4}>
          <Card
            style={{ minHeight: 260 }}
            className="dashboard_redeemed_chart card_100_24"
          >
            <CardBody className="text-center mb-3">
              <CardTitle>{t("Eidiyah Sales Trend")}</CardTitle>
              {loading || isLoading ? (
                <ShimmerThumbnail height={320} rounded />
              ) : (
                <div style={{ direction: "ltr" }}>
                  <ReactApexChart
                    options={lineChartConfig}
                    series={lineChartData}
                    type="line"
                    height={300}
                    className="apex-charts"
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={4}>
          <Card style={{ minHeight: 260 }} className="dashboard_redeemed_chart">
            <CardBody>
              <CardTitle className="text-center mb-3">
                {t("Redeemed vs. Unredeemed")}
              </CardTitle>
              {loading ? (
                <ShimmerThumbnail height={320} rounded />
              ) : (
                <div style={{ direction: "ltr" }} className="pie_chart">
                  <Pie data={pieData} options={pieChartOptions} />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={4}>
          <Card style={{ minHeight: 260 }} className="card_100_24">
            <CardBody>
              <CardTitle className="text-center mb-3">
                {t("Top Eidiyah Value")}
              </CardTitle>
              {loading ? (
                <ShimmerThumbnail height={320} rounded />
              ) : (
                <div style={{ direction: "ltr" }}>
                  <ReactApexChart
                    options={chartOptions}
                    series={series}
                    type="bar"
                    height={260}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {!loading && (
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("Eidiyah Metrics")}</CardTitle>
                {!loading && ediyahDashboardData?.groupedData?.length === 0 ? (
                  <p className="text-center my-3">{t("No data found")}</p>
                ) : (
                  <Table hover>
                    <thead className="table-light">
                      <tr>
                        <th>{t("Eidiyah Name")}</th>
                        <th>{t("Eidiyah Amount")}</th>
                        <th>{t("Total Purchase")}</th>
                        <th>{t("Total Purchase Amount")}</th>
                        <th>{t("Total Redeem")}</th>
                        <th>{t("Total Redeem Amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ediyahDashboardData?.groupedData?.map(item => (
                        <tr key={item?._id?.giftCardId}>
                          <td>
                            {language === "sa" ? item?.nameAr : item?.name}
                          </td>
                          <td>{item?.giftCardAmount}</td>
                          <td>{item?.purchasedCount}</td>
                          <td>{item?.totalPurchaseAmount}</td>
                          <td>{item?.redeemedCount}</td>
                          <td>{item?.redeemedAmount}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <th>{t("Total")}</th>
                        <th>
                          {getTotal(
                            ediyahDashboardData?.groupedData,
                            "giftCardAmount"
                          )}
                        </th>
                        <th>
                          {getTotal(
                            ediyahDashboardData?.groupedData,
                            "purchasedCount"
                          )}
                        </th>
                        <th>
                          {getTotal(
                            ediyahDashboardData?.groupedData,
                            "totalPurchaseAmount"
                          )}
                        </th>
                        <th>
                          {getTotal(
                            ediyahDashboardData?.groupedData,
                            "redeemedCount"
                          )}
                        </th>
                        <th>
                          {getTotal(
                            ediyahDashboardData?.groupedData,
                            "redeemedAmount"
                          )}
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {!loading && (
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Bar
                  width={550}
                  height={180}
                  data={barData}
                  options={barOptions}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  )
}

export default GiftCard
